/*
  (c) 2025 Fluxonix Inc.
*/

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { useAuth } from "../auth/AuthProvider";
import axios from "axios";
import { api } from "../api";

function postLoginDestination(fallback) {
  const value = sessionStorage.getItem("fluxonix_post_login_path");
  if (value && value.startsWith("/secure/")) {
    sessionStorage.removeItem("fluxonix_post_login_path");
    return value;
  }
  return fallback;
}

const ROLE_ROUTES = {
  admin: "/secure/assist",
  reviewer: "/secure/assist",
  lpo_submitter: "/lpo-form",
  field_agent: "/secure/assist",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [restoringSession, setRestoringSession] = useState(
    () => Boolean(window._fluxonix_msal?.getAllAccounts().length),
  );

  const otpRefs = useRef([]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      if (!active) return;
      active = false;
      controller.abort();
      setRestoringSession(false);
      setError("Your session could not be restored. Please sign in again.");
    }, 15000);

    const initAuth = async () => {
      try {
        for (let i = 0; i < 10; i++) {
          if (!active) return;
          if (window._fluxonix_msal) break;
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
        if (!active) return;
        const msal = window._fluxonix_msal;
        const accounts = msal?.getAllAccounts() || [];
        if (!accounts.length) return;
        setRestoringSession(true);
        const cachedEmail = sessionStorage.getItem("fluxonix_last_email");
        const account = accounts.find((a) => a.username === cachedEmail) || accounts[0];
        const tokenResp = await msal.acquireTokenSilent({
          account,
          scopes: ["openid", "profile", "email", "api://8fcde660-f8cd-4f26-b3e4-94d804be561f/access_as_user"],
        });
        if (!active) return;
        const jwt = tokenResp.accessToken;
        const res = await api.get("/auth/me", {
          headers: { Authorization: `Bearer ${jwt}` },
          signal: controller.signal,
        });
        if (!active) return;
        login(res.data, jwt);
        const target = ROLE_ROUTES[res.data.role] || "/secure/assist";
        navigate(postLoginDestination(target));
      } catch (err) {
        if (active) {
          setError("Your session could not be restored. Please sign in again.");
        }
      } finally {
        clearTimeout(timeout);
        if (active) setRestoringSession(false);
      }
    };

    initAuth();
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [login, navigate]);

  const handleEntraLogin = async () => {
    if (!email) return setError("Please enter your work email");
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`/auth/config?email=${encodeURIComponent(email)}`);
      const cfg = res.data;

      if (!cfg.authority || !cfg.client_id) {
        throw new Error("This organization is not configured for Entra login");
      }

      if (cfg.tenant_slug) {
        sessionStorage.setItem("inferred_slug", cfg.tenant_slug);
      }

      let authority = cfg.authority;
      if (authority.endsWith("/v2.0")) authority = authority.replace(/\/v2\.0$/, "");

      const msal = new PublicClientApplication({
        auth: {
          clientId: cfg.client_id,
          authority,
          redirectUri:
            process.env.NODE_ENV === "production"
              ? "https://app.fluxonix.io/auth/callback"
              : window.location.origin,
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      });

      window._fluxonix_msal = msal;
      sessionStorage.setItem("fluxonix_last_email", email);

      await msal.loginRedirect({
        scopes: ["openid", "profile", "email", "api://8fcde660-f8cd-4f26-b3e4-94d804be561f/access_as_user"],
        prompt: "select_account",
      });
    } catch (err) {
      setError(
        err.response?.data?.detail
        || err.message
        || "Unable to start login"
      );
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!email) {
      setError("Please enter your work email");
      return;
    }
    setLoading(true);
    setError("");

    try {
      if (email.toLowerCase().endsWith("@mailer.banyannation.com")) {
        sessionStorage.setItem("inferred_slug", "banyan");
      }
      await api.post("/auth/send-otp", { email });
      setOtpSent(true);
      setInfo("OTP sent to your email.");
    } catch (err) {
      setError("Failed to send OTP. Please check the email and try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/verify-otp", {
        email,
        otp: otpString,
      });

      const { access_token } = response.data;
      const userData = response.data?.user;

      let decoded = {};
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1]));
        decoded = payload;
      } catch (e) {
        console.error("Failed to decode JWT:", e);
      }

      const isLpo = userData.user_type === "lpo" || !!decoded.lpo_id;

      let derivedRoles = [];
      if (userData.role === "b2c_user") {
        derivedRoles = ["admin", "field_agent"];
      } else if (Array.isArray(userData.role)) {
        derivedRoles = userData.role;
      } else if (userData.role) {
        derivedRoles = [userData.role];
      }

      const normalized = {
        ...userData,
        lpo_id: userData.lpo_id ?? decoded.lpo_id ?? null,
        user_id: decoded.user_id ?? null,
        userType: isLpo ? "lpo" : "user",
        identityKey: isLpo ? "lpo_id" : "user_id",
        identityValue: isLpo ? decoded.lpo_id : decoded.user_id,
        roles: derivedRoles,
        permissions: Array.isArray(userData.permissions)
          ? userData.permissions
          : userData.permissions
          ? [userData.permissions]
          : [],
      };

      if (email.endsWith("@mailer.banyannation.com")) {
        localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
        localStorage.setItem("lpo_token", access_token);
        login(normalized, access_token);
        navigate(postLoginDestination("/lpo-entry-mode"));
      } else {
        localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
        localStorage.setItem("fluxonix_token", access_token);
        login(normalized, access_token);
        navigate(postLoginDestination("/secure/assist"));
      }
    } catch (err) {
      setError("Invalid OTP or expired. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const nextEmptyIndex = newOtp.findIndex((val) => !val);
      otpRefs.current[nextEmptyIndex === -1 ? 5 : nextEmptyIndex]?.focus();
    }
  };

if (restoringSession) {
  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen flex items-center justify-center px-4 py-8 sm:py-10">
      <div className="w-full max-w-md bg-white dark:bg-slate-900/50 p-8 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 text-center">
        <div className="mx-auto mb-4 h-8 w-8 rounded-full border-4 border-primary/20 border-t-primary animate-spin" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Completing sign-in</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Taking you to your Fluxonix workspace…</p>
      </div>
    </div>
  );
}

return (
  <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen flex items-center justify-center px-4 py-8 sm:py-10">
    {/* Card */}
    <div className="w-full max-w-md bg-white dark:bg-slate-900/50 p-6 sm:p-8 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
      <div className="flex flex-col items-center mb-6 sm:mb-8">
        <div className="bg-primary/10 p-3 rounded-lg mb-4">
          <span className="material-symbols-outlined text-primary text-4xl">shield_lock</span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Fluxonix</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Enterprise Access Management</p>
      </div>

      <div className="space-y-5 sm:space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold">Welcome back</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">
            Secure access with automated role-based routing
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {info && (
          <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 p-3 rounded-lg text-sm">
            {info}
          </div>
        )}

        {/* Shared email identity FIRST (used for Entra config OR OTP) */}
        <form className="space-y-4" onSubmit={verifyOtp}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 ml-1">
              Work Email
            </label>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-60"
              disabled={loading} // allow editing even after otpSent if you want; change to (otpSent || loading) to lock
            />

            <p className="text-[11px] text-slate-400 dark:text-slate-500 ml-1">
              Enter your work email to continue. Use Entra ID or request a 6-digit OTP.
            </p>
          </div>

          {/* Choose sign-in method */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handleEntraLogin}
              disabled={loading || !email}
              className="w-full flex items-center justify-center gap-0 h-11 rounded border border-[#8c8c8c] bg-white hover:bg-[#f3f3f3] active:bg-[#eaeaea] transition-colors disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              style={{ fontFamily: "'Segoe UI', sans-serif" }}
            >
              {/* Microsoft logo block */}
              <span className="flex items-center justify-center w-11 h-full bg-white flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
              </span>
              <span className="flex-1 pr-3 text-[15px] font-semibold text-[#5e5e5e] tracking-[0.01em] text-center">
                Sign in with Microsoft
              </span>
            </button>

            <button
              type="button"
              onClick={sendOtp}
              disabled={loading || !email}
              className="w-full h-11 px-4 rounded-lg text-sm font-semibold text-primary border border-primary/20 hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && !otpSent ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
            </button>
          </div>

          {/* OTP */}
          {otpSent && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 ml-1">
                One-Time Password
              </label>

              <div className="grid grid-cols-6 gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    className="w-full aspect-square text-center text-lg font-bold border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                ))}
              </div>

              <p className="text-[11px] text-slate-400 dark:text-slate-500 ml-1">
                Didn’t receive it? Check spam, or click “Resend OTP”.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !otpSent || otp.join("").length !== 6}
            className="w-full bg-primary hover:bg-primary/90 text-white h-12 rounded-lg font-semibold shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Continue"}
            <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </form>
      </div>

      <div className="mt-6 sm:mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
          <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            Fluxonix utilizes dynamic role-based routing. Upon successful authentication, you will be redirected to the environment provisioned for your identity profile.
          </p>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 text-center">
        <p className="text-xs text-slate-400 dark:text-slate-500">© 2025 Fluxonix Security. All rights reserved.</p>
      </div>
    </div>

    {/* Background glow */}
    <div className="fixed inset-x-0 bottom-0 -z-10 h-56 sm:h-64 pointer-events-none opacity-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
      <div className="absolute -bottom-28 -left-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
      <div className="absolute -bottom-28 -right-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
    </div>
  </div>
);
}
